'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { client, HOURS_REPORT, HOURS_REPORT_TOTALS, linePrice, type ReportStatus } from '../demo-data'
import { activityLabel, useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AppSubHeader } from './app-screen'
import styles from './reports-visual.module.css'
import { useScenario } from './use-scenario'

/** The selection the card is about: work that is billable and not yet on an invoice. */
const FILTER: ReportStatus = 'te-factureren'

const statusLabel = (t: VisualCopy, status: ReportStatus) => t.content.reportStatus[status]

const STATUS_CLASS: Record<ReportStatus, string> = {
  'te-factureren': styles.pillOpen,
  gefactureerd: styles.pillDone,
  'niet-facturabel': styles.pillNone,
}

const columns = (t: VisualCopy) => [
  { label: t.word.workItem.toUpperCase(), align: 'left' as const, className: styles.hideNarrow },
  { label: t.word.amount.toUpperCase(), align: 'right' as const, className: styles.hideNarrow },
  { label: t.word.price.toUpperCase(), align: 'right' as const },
  { label: t.word.status.toUpperCase(), align: 'left' as const },
]

/**
 * Seven beats, each the same length.
 *
 * The story is short — filter, narrow, export — and it was told over fourteen beats of which five
 * changed nothing at all. What was left, once those were gone, is seven steps of roughly equal
 * weight, and giving each of them the same time is what makes the card read as one steady sequence
 * rather than as a thing that hurries and then waits.
 */
const TOTAL_BEATS = 7
const BEAT_MS = 1250

// Beat 5: the filtered report with the export confirmed. The still used to be the same picture
// without the toast, on the reasoning that a transient confirmation frozen forever reads as a
// glitch. It reads instead as the end of the story: filtered, exported, and here is the proof.
const REST_BEAT = 5
const RESET_BEAT = 6 // final loop beat: already reads as the unfiltered start

const FILTER_OPEN_AT = 1
// The totals narrow on the same beat the rows drop out: a header that still counts the whole month
// beside a filtered list is a screen contradicting itself, even for one beat.
const FILTERED_FROM = 2
const TOTALS_FROM = FILTERED_FROM
const ACTIONS_FROM = 3
const EXPORTED_FROM = 4
const CLEARED_FROM = 6

/** Resolves `beat` (SSR/reduced-motion `null` → the filtered rest) to the beat state is derived
 *  from; the final loop beats already read as the unfiltered start, for a seamless wrap. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

/** The board's filter control — a funnel, the same mark `PlanningWallVisual` puts on its bar. */
function FunnelIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M2.5 3.5h11l-4.2 5v4l-2.6 1.4V8.5L2.5 3.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function StatusPill({ status }: { status: ReportStatus }) {
  const { t } = useVisualCopy()
  return <span className={clsx(styles.pill, STATUS_CLASS[status])}>{t.content.reportStatus[status]}</span>
}

/**
 * Rapportages — the hours report, and the one question it answers in a click: what has been worked
 * and not yet invoiced. The status column is filtered on "te factureren", the lines that do not
 * match drop out, and the three figures in the header — lines, hours, euros — narrow with them,
 * from the whole month to what is still to be billed. Then Acties → Exporteren hands that selection
 * to Excel. Modelled on the app's Rapportages → Urenregistratie → Uren screen, where those totals
 * sit beside the date range and move with every filter.
 *
 * Driven by `useScenario`'s 14-beat loop; `beat === null` (SSR, no-JS, `prefers-reduced-motion:
 * reduce`) renders the resolved rest state: filtered, totals narrowed, the export confirmed.
 */
export function ReportsVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: BEAT_MS, restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const filterOpen = effBeat >= FILTER_OPEN_AT && effBeat < CLEARED_FROM
  const filtered = effBeat >= FILTERED_FROM && effBeat < CLEARED_FROM
  const narrowed = effBeat >= TOTALS_FROM && effBeat < CLEARED_FROM
  const actionsOpen = effBeat >= ACTIONS_FROM && effBeat < EXPORTED_FROM
  const exported = effBeat >= EXPORTED_FROM && effBeat < CLEARED_FROM

  const totals = narrowed ? HOURS_REPORT_TOTALS.toInvoice : HOURS_REPORT_TOTALS.all

  return (
    <AppUiVisual label={t.screen.reports.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="reports" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.reports.title}
            detail={t.period.month}
            right={
              <span className={clsx(styles.actions, actionsOpen && styles.actionsOn)}>
                {t.screen.reports.actions} <span aria-hidden>▾</span>
              </span>
            }
          />

          {/* The planning wall's bar, and now the same bar: the funnel on the left, what it is
              narrowed to as a chip you could click away beside it, and on the right what the
              narrowed set adds up to — where that board puts its client count.

              The three figures had a band of their own under this one, which made two rules and two
              strips of chrome out of one line of a screen. They count the month, not the view, and
              they move with the filter; that is the same fact the chip states, so they belong on the
              same line as it. */}
          <div className={styles.filterBar}>
            <span className={clsx(styles.filterIcon, filterOpen && styles.filterIconActive)} aria-hidden="true">
              <FunnelIcon />
            </span>
            <div className="flex min-w-0 flex-1 items-center">
              <span className={clsx(styles.chip, filterOpen && styles.chipVisible)}>
                {t.word.status}: {statusLabel(t, FILTER)}
              </span>
            </div>
            <div className={styles.totals}>
              <span className={clsx(styles.total, styles.totalWide, narrowed && styles.totalOn)}>
                <b>{totals.results}</b> {t.screen.reports.rows}
              </span>
              <span className={clsx(styles.total, narrowed && styles.totalOn)}>
                <b>{totals.hours}</b> {t.word.hoursWord}
              </span>
              <span className={clsx(styles.total, narrowed && styles.totalOn)}>
                <b>{fmt.euro(totals.amount, true)}</b>
              </span>
            </div>
          </div>

          <AppSubHeader rowLabel={t.word.client.toUpperCase()} columns={columns(t)} className={styles.head} />

          <div className={styles.rows}>
            {HOURS_REPORT.map((row) => {
              const dropped = filtered && row.status !== FILTER
              return (
                <div
                  key={`${row.day}-${row.nr}-${row.activity}`}
                  className={clsx(styles.row, dropped && styles.rowOut)}
                >
                  <div className={styles.name}>
                    <span className={styles.date}>{fmt.shortDate(row.day)}</span>
                    <span className={styles.clientName}>{client(row.nr).short}</span>
                  </div>
                  <span className={styles.activity}>{activityLabel(t, row.activity)}</span>
                  <span className={styles.hours}>
                    {fmt.decimal(row.hours, 2)}
                    <span className={styles.hoursUnit}> {t.word.hoursUnit}</span>
                  </span>
                  <span className={styles.price}>{row.rate === 0 ? '—' : fmt.euro(linePrice(row))}</span>
                  <StatusPill status={row.status} />
                </div>
              )
            })}
          </div>

          {/* Acties → Exporteren: the selection leaves as a file, which is what a report is for. */}
          <div className={clsx(styles.menu, actionsOpen && styles.menuOn)} aria-hidden="true">
            <div className={styles.menuRow}>{t.screen.reports.changeStatus}</div>
            <div className={clsx(styles.menuRow, styles.menuRowActive)}>
              {t.screen.reports.export} <span className={styles.menuHint}>{t.screen.reports.excel}</span>
            </div>
            <div className={styles.menuRow}>{t.screen.reports.changeColumns}</div>
          </div>

          <div className={clsx(styles.toast, exported && styles.toastOn)} aria-hidden="true">
            <span className={styles.toastIcon}>✓</span>
            {t.screen.reports.exported(totals.results)}
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
